/**
 * Calendar Sync Service for External Calendar Integration.
 *
 * Manages polling for external calendar updates, pushing local changes,
 * retry logic on failure, and sync status tracking. Works in conjunction
 * with the OAuthService for authentication and the backend sync handler
 * for data exchange.
 *
 * - Polls connected calendars every 5 minutes while online
 * - Retries failed sync up to 3 times at 30-second intervals
 * - Emits 'calendar:event-created' events via the event bus for new events
 * - Stores sync results locally for UI display
 *
 * @module calendarSyncService
 * @see Requirements 6.3, 6.4, 6.5, 6.6, 6.8
 */

import type { CalendarEvent, ExternalCalendarConnection } from '../types/calendar';
import { getIdToken } from './auth';
import { eventBus } from './eventBus';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Polling interval for external calendar sync: 5 minutes in milliseconds. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Retry delay on sync failure: 30 seconds in milliseconds. */
const RETRY_DELAY_MS = 30 * 1000;

/** Maximum number of retry attempts on sync failure. */
const MAX_RETRIES = 3;

/** Maximum number of external calendar connections allowed. */
const MAX_CONNECTIONS = 5;

// ─── Types ───────────────────────────────────────────────────────────────────

/** Result of a sync operation for a single connection. */
export interface SyncResult {
  /** The connection ID that was synced. */
  connectionId: string;
  /** Whether the sync was successful. */
  success: boolean;
  /** Number of new events received from the provider. */
  eventsReceived: number;
  /** Number of events pushed to the provider. */
  eventsPushed: number;
  /** Error message if the sync failed. */
  error?: string;
  /** ISO 8601 timestamp of the sync attempt. */
  syncedAt: string;
}

/** Result of pushing local changes to an external provider. */
export interface PushResult {
  /** Whether the push was successful. */
  success: boolean;
  /** Number of events successfully pushed. */
  pushedCount: number;
  /** IDs of events that failed to push. */
  failedEventIds: string[];
  /** Error message if the push failed. */
  error?: string;
}

/** Status information for a calendar connection's sync state. */
export interface ConnectionSyncStatus {
  /** ISO 8601 timestamp of last successful sync, or null if never synced. */
  lastSyncAt: string | null;
  /** Current sync status. 'revoked' indicates token expiry requiring re-authorization. */
  status: 'idle' | 'syncing' | 'error' | 'retrying' | 'revoked';
  /** Error message if in error state. */
  errorMessage?: string;
  /** Number of retries attempted for the current failure. */
  retryCount: number;
}

/** Options for creating the CalendarSyncService (useful for testing). */
export interface CalendarSyncServiceOptions {
  /** Base URL for the Kira API. Defaults to VITE_API_URL env var or '/api'. */
  apiBaseUrl?: string;
  /** Custom fetch implementation (for testing). */
  fetchFn?: typeof fetch;
  /** Custom poll interval in ms (for testing). */
  pollIntervalMs?: number;
  /** Custom retry delay in ms (for testing). */
  retryDelayMs?: number;
  /** Custom delay function (for testing). */
  delayFn?: (ms: number) => Promise<void>;
}

/** Calendar Sync Service interface. */
export interface CalendarSyncService {
  /** Start the polling cycle for all connected calendars. */
  startPolling(): void;
  /** Stop the polling cycle. */
  stopPolling(): void;
  /** Manually trigger a sync for a specific connection. */
  syncNow(connectionId: string): Promise<SyncResult>;
  /** Push local event changes to an external provider. */
  pushChanges(
    connectionId: string,
    events: Array<{ eventId: string; action: 'create' | 'update' | 'delete' }>
  ): Promise<PushResult>;
  /** Get the last sync status for all connections. */
  getLastSyncStatus(): Map<string, ConnectionSyncStatus>;
  /** Check if polling is currently active. */
  isPolling(): boolean;
}

// ─── Calendar Sync Service Implementation ────────────────────────────────────

/**
 * Creates a CalendarSyncService instance that manages polling and sync
 * for external calendar connections.
 */
export function createCalendarSyncService(
  options: CalendarSyncServiceOptions = {}
): CalendarSyncService {
  const apiBaseUrl = options.apiBaseUrl ?? import.meta.env.VITE_API_URL ?? '/api';
  const fetchFn = options.fetchFn ?? fetch;
  const pollIntervalMs = options.pollIntervalMs ?? POLL_INTERVAL_MS;
  const retryDelayMs = options.retryDelayMs ?? RETRY_DELAY_MS;
  const delayFn =
    options.delayFn ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  /** Polling timer reference. */
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  /** Whether polling is active. */
  let polling = false;

  /** Sync status per connection. */
  const syncStatusMap = new Map<string, ConnectionSyncStatus>();

  // ─── API Helpers ─────────────────────────────────────────────────────────

  /**
   * Make an authenticated API request to the backend.
   */
  async function apiRequest<T>(
    path: string,
    method: string = 'GET',
    body?: Record<string, unknown>
  ): Promise<T> {
    const token = await getIdToken();
    if (!token) {
      throw new Error('Not authenticated. Please log in to sync calendars.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    const response = await fetchFn(`${apiBaseUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(
        `Sync request failed: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`
      );
    }

    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return response.json() as Promise<T>;
  }

  /**
   * Fetch active connections from the backend.
   */
  async function fetchConnections(): Promise<ExternalCalendarConnection[]> {
    const result = await apiRequest<{ connections: ExternalCalendarConnection[] }>(
      '/calendar/connections'
    );
    return result.connections.filter((c) => c.status === 'active');
  }

  // ─── Sync Logic ──────────────────────────────────────────────────────────

  /**
   * Initialize sync status for a connection if not already tracked.
   */
  function ensureStatus(connectionId: string): ConnectionSyncStatus {
    if (!syncStatusMap.has(connectionId)) {
      syncStatusMap.set(connectionId, {
        lastSyncAt: null,
        status: 'idle',
        retryCount: 0,
      });
    }
    return syncStatusMap.get(connectionId)!;
  }

  /**
   * Check if an error indicates a token expiry / revocation (HTTP 401).
   */
  function isTokenExpiredError(error: unknown): boolean {
    if (error instanceof Error) {
      return error.message.includes('401');
    }
    return false;
  }

  /**
   * Attempt to sync a single connection with retry logic.
   * Retries up to 3 times at 30-second intervals on failure.
   * If a 401 is received, marks the connection as 'revoked' immediately
   * (no retries) to prompt re-authorization.
   */
  async function syncWithRetry(connectionId: string): Promise<SyncResult> {
    const status = ensureStatus(connectionId);
    status.status = 'syncing';
    status.retryCount = 0;
    status.errorMessage = undefined;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const result = await performSync(connectionId);

        // Success — update status
        status.status = 'idle';
        status.lastSyncAt = result.syncedAt;
        status.retryCount = 0;
        status.errorMessage = undefined;

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown sync error';

        // Token expiry (401) — mark as revoked immediately, no retries
        if (isTokenExpiredError(error)) {
          status.status = 'revoked';
          status.retryCount = 0;
          status.errorMessage = 'Authorization expired. Please re-connect your calendar.';

          return {
            connectionId,
            success: false,
            eventsReceived: 0,
            eventsPushed: 0,
            error: 'Authorization expired. Please re-connect your calendar.',
            syncedAt: new Date().toISOString(),
          };
        }

        if (attempt < MAX_RETRIES) {
          // Retry after delay
          status.status = 'retrying';
          status.retryCount = attempt + 1;
          status.errorMessage = errorMessage;

          await delayFn(retryDelayMs);
        } else {
          // All retries exhausted — surface error
          status.status = 'error';
          status.retryCount = MAX_RETRIES;
          status.errorMessage = errorMessage;

          return {
            connectionId,
            success: false,
            eventsReceived: 0,
            eventsPushed: 0,
            error: errorMessage,
            syncedAt: new Date().toISOString(),
          };
        }
      }
    }

    // This should not be reached, but TypeScript requires it
    return {
      connectionId,
      success: false,
      eventsReceived: 0,
      eventsPushed: 0,
      error: 'Unexpected sync flow termination',
      syncedAt: new Date().toISOString(),
    };
  }

  /**
   * Perform a single sync request for a connection (no retry logic).
   */
  async function performSync(connectionId: string): Promise<SyncResult> {
    const response = await apiRequest<{
      events: CalendarEvent[];
      syncToken: string | null;
    }>(`/calendar/sync/${connectionId}`, 'POST');

    const newEvents = response.events || [];

    // Emit events for each new calendar event received
    for (const event of newEvents) {
      eventBus.emit('calendar:event-created', {
        eventId: event.eventId,
        date: event.startDate.split('T')[0] ?? event.startDate,
      });
    }

    return {
      connectionId,
      success: true,
      eventsReceived: newEvents.length,
      eventsPushed: 0,
      syncedAt: new Date().toISOString(),
    };
  }

  /**
   * Poll all active connections. Called on interval.
   */
  async function pollAllConnections(): Promise<void> {
    try {
      const connections = await fetchConnections();

      // Enforce max connections (should be enforced server-side too)
      const activeConnections = connections.slice(0, MAX_CONNECTIONS);

      // Sync each connection (sequentially to avoid overwhelming the backend)
      for (const connection of activeConnections) {
        if (!polling) break; // Stop if polling was disabled while syncing
        await syncWithRetry(connection.connectionId);
      }
    } catch {
      // If fetching connections fails (e.g., network error), skip this poll cycle
    }
  }

  // ─── Public Interface ────────────────────────────────────────────────────

  function startPolling(): void {
    if (polling) return;

    polling = true;

    // Perform an immediate sync on start
    void pollAllConnections();

    // Set up recurring poll
    pollTimer = setInterval(() => {
      void pollAllConnections();
    }, pollIntervalMs);
  }

  function stopPolling(): void {
    polling = false;

    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  async function syncNow(connectionId: string): Promise<SyncResult> {
    return syncWithRetry(connectionId);
  }

  async function pushChanges(
    connectionId: string,
    events: Array<{ eventId: string; action: 'create' | 'update' | 'delete' }>
  ): Promise<PushResult> {
    try {
      const result = await apiRequest<{
        pushedCount: number;
        failedEventIds: string[];
      }>(`/calendar/sync/${connectionId}/push`, 'POST', {
        events,
      });

      return {
        success: result.failedEventIds.length === 0,
        pushedCount: result.pushedCount,
        failedEventIds: result.failedEventIds,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to push changes';

      return {
        success: false,
        pushedCount: 0,
        failedEventIds: events.map((e) => e.eventId),
        error: errorMessage,
      };
    }
  }

  function getLastSyncStatus(): Map<string, ConnectionSyncStatus> {
    return new Map(syncStatusMap);
  }

  function isPollingActive(): boolean {
    return polling;
  }

  return {
    startPolling,
    stopPolling,
    syncNow,
    pushChanges,
    getLastSyncStatus,
    isPolling: isPollingActive,
  };
}

// ─── Singleton Instance ──────────────────────────────────────────────────────

/** Singleton Calendar Sync service instance for the Kira app. */
export const calendarSyncService = createCalendarSyncService();
